import { GeneralArrowLeftIcon, GeneralArrowRightIcon } from '@outdoorsyco/bonfire';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import Button, {
  ButtonShape,
  ButtonSize,
  ButtonVariants,
} from '@/components/switchback/Button/Button';
import { ARROW_STOP } from '@/components/switchback/Icon/assets';
import Icon, { IconSize } from '@/components/switchback/Icon/IconComponent';

import css from './ProgressivePagination.module.css';

export const enum EProgressivePaginationActions {
  next,
  prev,
  first,
  page,
}

interface IProgressivePaginationProps {
  onProgressivePagination: (action: EProgressivePaginationActions, page?: number) => void;
  stopPosition: number;
  startPosition: number;
  testIdPrefix: string;
  totalItems: number;
  isSearchLoading: boolean;
  itemsPerPage: number;
  visitedPages: number[];
  currentPage: number;
}

interface IButton {
  type: string;
  dataType: 'next' | 'previous' | 'first';
  handleClick: () => void;
  arrow_type: 'normal' | 'stop';
  disabled: boolean;
}

type IProgressivePaginationElement = React.HTMLAttributes<HTMLDivElement>;

const ProgressivePagination: React.FC<
  IProgressivePaginationProps & IProgressivePaginationElement
> = ({
  onProgressivePagination,
  stopPosition,
  startPosition,
  testIdPrefix,
  totalItems,
  isSearchLoading,
  visitedPages,
  currentPage,
  ...rest
}) => {
  const intl = useIntl();

  const { pagesWithEllipsis, shouldShowEllipsis } = useMemo(() => {
    // Sort pages by page number for display
    const sortedPages = [...visitedPages].sort((a, b) => a - b);

    // If we have more than 6 pages visited, show ellipsis + last 6
    const hasEllipsis = sortedPages.length > 6;
    const displayPages = hasEllipsis ? sortedPages.slice(-6) : sortedPages;

    return { pagesWithEllipsis: displayPages, shouldShowEllipsis: hasEllipsis };
  }, [visitedPages]);

  const buttonTemplate = ({ type, dataType, handleClick, arrow_type, disabled }: IButton) => {
    // Use Switchback component for the first button
    if (arrow_type === 'stop') {
      return (
        <Button
          aria-label={intl.formatMessage(
            {
              defaultMessage: 'Go to {type} page',
              id: 'R4vmX5',
            },
            {
              type,
            },
          )}
          disabled={disabled}
          className={`mr-4 last:mr-0 ${css.arrow}`}
          onClick={handleClick}
          shape={ButtonShape.circle}
          variant={ButtonVariants.whiteContained}
          size={ButtonSize.small}
          data-testid={`${testIdPrefix}-${dataType}-btn`}>
          <Icon
            data-icon={dataType}
            className={`${css.icon} ${css.firstIcon}`}
            name={ARROW_STOP}
            size={IconSize.normal}
          />
        </Button>
      );
    }

    // Use Bonfire components for other buttons
    const getIconComponent = () => {
      if (dataType === 'previous') {
        return <GeneralArrowLeftIcon fontSize={16} />;
      }
      return <GeneralArrowRightIcon fontSize={16} />;
    };

    return (
      <button
        aria-label={intl.formatMessage(
          {
            defaultMessage: 'Go to {type} page',
            id: 'R4vmX5',
          },
          {
            type,
          },
        )}
        disabled={disabled}
        className={`mr-4 last:mr-0 ${css.arrowButton}`}
        onClick={handleClick}
        data-testid={`${testIdPrefix}-${dataType}-btn`}>
        {getIconComponent()}
      </button>
    );
  };

  const handlePageClick = (page: number) => {
    onProgressivePagination(EProgressivePaginationActions.page, page);
  };

  return (
    <nav aria-label="pagination navigation" {...rest}>
      <ul className="flex content-center justify-center md:justify-start gap-1">
        {/* First button */}
        <li className="flex items-center justify-center">
          {buttonTemplate({
            type: intl.formatMessage({ defaultMessage: 'first', id: 'f92A29' }),
            dataType: 'first',
            handleClick: () => onProgressivePagination(EProgressivePaginationActions.first),
            arrow_type: 'stop',
            disabled: startPosition === 1,
          })}
        </li>

        {/* Previous button */}
        <li className="flex items-center justify-center">
          {buttonTemplate({
            type: intl.formatMessage({ defaultMessage: 'previous', id: '7Gf6K9' }),
            dataType: 'previous',
            handleClick: () => onProgressivePagination(EProgressivePaginationActions.prev),
            arrow_type: 'normal',
            disabled: startPosition === 1,
          })}
        </li>

        {/* Page numbers */}
        {shouldShowEllipsis && (
          <li className="flex items-center justify-center">
            <button
              disabled
              className={`flex flex-col justify-center h-full px-3 md:px-4 ${css.button}`}>
              …
            </button>
          </li>
        )}
        {pagesWithEllipsis.map(page => (
          <li key={page} className="flex items-center justify-center">
            <button
              aria-current={page === currentPage}
              aria-label={intl.formatMessage(
                {
                  defaultMessage: 'Go to page {page}',
                  id: 'ox3QYb',
                  description: 'Pagination go to aria-label',
                },
                {
                  page,
                },
              )}
              className={`flex flex-col justify-center h-full px-3 md:px-4 ${css.button}`}
              data-variant={page === currentPage ? 'current' : ''}
              data-testid={`${testIdPrefix}-${page}-page${page === currentPage ? '-selected' : ''}`}
              type="button"
              onClick={() => handlePageClick(page)}
              disabled={isSearchLoading || page === currentPage}>
              {page}
            </button>
          </li>
        ))}

        {/* Next button */}
        <li className="flex items-center justify-center">
          {buttonTemplate({
            type: intl.formatMessage({ defaultMessage: 'next', id: 'izd/bu' }),
            dataType: 'next',
            handleClick: () => onProgressivePagination(EProgressivePaginationActions.next),
            arrow_type: 'normal',
            disabled: stopPosition === totalItems || isSearchLoading,
          })}
        </li>
      </ul>
    </nav>
  );
};

export default ProgressivePagination;
